import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import I18n from "../../i18n"
import { escapeXml as escapeHtml, sheetSvg } from "./mockup_sheet.mjs"

const t = (key, options) => I18n.t(`erp.sales.js.mockup.${key}`, options)

const FACES = ["front", "back"]
const BOARD_LIMIT = 6
// Low definition on purpose: the mock-up is an indicative preview, not a BAT
const FILE_MAX_PX = 600
const SHEET_MAX_WIDTH = 1100
const INK = "#1C1B18"
const SVG_NS = "http://www.w3.org/2000/svg"

const cm = (value) => Number(value).toLocaleString("fr-FR", { maximumFractionDigits: 1 })
const SELECT = "erp-select h-9 w-full rounded-erp-field border border-erp-line-strong bg-erp-field px-2.5 text-[13px] focus:border-erp-black focus:outline-none"
const option = (value, label, selected, disabled = false) => `<option value="${escapeHtml(value)}"${selected ? " selected" : ""}${disabled ? " disabled" : ""}>${escapeHtml(label)}</option>`
const markParams = (mark) => `data-erp--mockup-key-param="${escapeHtml(mark.key)}"`
const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image()
  image.onload = () => resolve(image)
  image.onerror = reject
  image.src = src
})

// Mock-up of an estimate (spec PB-3): draws the views of Mockups::Builder on the templates of
// Mockups::TemplateOptions. Everything chosen here (line, file, placements, sheet) lives for the session only; the
// customer files are drawn as <image> from their blob, through a canvas that lowers their definition and removes
// their white background, never inlined as SVG. The sheet exports as a PNG, downloaded or attached to a draft.
export default class extends Controller {
  static targets = ["stage", "stageTitle", "stageSubtitle", "faces", "lines", "files", "marks", "restore", "addMark",
    "addToBoard", "board", "boardCount", "status", "attach", "removeBackground"]

  static values = { payload: Object, directUploadUrl: String, attachUrl: String }

  connect() {
    const { estimate, views, templates } = this.payloadValue
    this.estimate = estimate
    this.views = views
    this.templates = templates
    this.lines = this.#groupLines(views)
    this.files = this.#estimateFiles()
    this.board = []
    this.renderCount = 0
    this.addedCount = 0
    this.sessionFileCount = 0
    this.sessions = new Map()
    this.#selectLine(this.lines[0].id)
    this.preparing = this.#prepareFiles([...this.files.values()])
  }

  // ---- Actions ----

  pickLine({ params: { id } }) {
    if (id !== this.line.id) this.#selectLine(id)
  }

  pickFace({ params: { face } }) {
    this.face = face
    this.#renderMarksAndStage()
  }

  addMark() {
    const zones = this.#template()?.faces
    if (!zones) return

    const taken = (face, code) => this.#occupied(face, code)
    const face = [this.face, ...FACES.filter((other) => other !== this.face)]
      .find((candidate) => zones[candidate].zones.some((zone) => !taken(candidate, zone.code)))
    if (!face) return this.#announce(t("all_zones_used"))

    const zone = zones[face].zones.find((candidate) => !taken(face, candidate.code))
    this.marks.push({ key: `added-${++this.addedCount}`, face, placed: true, added: true, fileKey: this.#defaultFileKey(), ...this.#zoneFormat(zone, zone.formats[0]) })
    this.face = face
    this.changed = true
    this.#renderMarksAndStage()
  }

  removeMark({ params: { key } }) {
    this.marks = this.marks.filter((mark) => mark.key !== key)
    this.changed = true
    this.#renderMarksAndStage()
  }

  changeZone({ target, params: { key } }) {
    const mark = this.#mark(key)
    const [face, code] = target.value.split("|")
    if (this.#occupied(face, code, mark)) return this.#renderMarks()

    const zone = this.#template().faces[face].zones.find((candidate) => candidate.code === code)
    const format = zone.formats.find((candidate) => candidate.code === mark.format) || zone.formats[0]
    Object.assign(mark, { face }, this.#zoneFormat(zone, format))
    this.face = face
    this.changed = true
    this.#renderMarksAndStage()
  }

  changeFormat({ target, params: { key } }) {
    const mark = this.#mark(key)
    const zone = this.#template().faces[mark.face].zones.find((candidate) => candidate.code === mark.zone.code)
    Object.assign(mark, this.#zoneFormat(zone, zone.formats.find((candidate) => candidate.code === target.value)))
    this.changed = true
    this.#renderMarksAndStage()
  }

  changeFile({ target, params: { key } }) {
    this.#mark(key).fileKey = target.value || null
    this.changed = true
    this.#renderMarksAndStage()
  }

  restore() {
    this.#resetMarks()
    this.#renderMarksAndStage()
    this.restoreTarget.focus()
  }

  async addFile({ target }) {
    const [upload] = target.files
    target.value = ""
    if (!upload) return

    const raw = await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.readAsDataURL(upload)
    })
    const file = { key: `session-${++this.sessionFileCount}`, filename: upload.name, raw, drawable: true, light: false, session: true }
    await this.#prepare(file)
    if (file.failed) return this.#announce(t("file_unreadable"))

    this.files.set(file.key, file)
    const mark = this.marks.find((candidate) => candidate.placed && candidate.face === this.face)
    if (mark) {
      mark.fileKey = file.key
      this.changed = true
    }
    this.#renderFiles()
    this.#renderMarksAndStage()
    this.#renderBoard()
    this.#announce(mark ? t("file_placed", { name: file.filename, zone: mark.zone.name }) : t("file_added", { name: file.filename }))
  }

  async toggleBackground() {
    this.preparing = this.#prepareFiles([...this.files.values()])
    await this.preparing
  }

  addToBoard() {
    if (!this.line.template || this.board.length >= BOARD_LIMIT) return

    this.board.push(this.#currentView())
    this.#renderBoard()
    this.#announce(t("board_added", { count: this.board.length, limit: BOARD_LIMIT }))
  }

  fillBoard() {
    const views = this.views.filter((view) => view.template).map((view) => {
      const line = this.lines.find((candidate) => candidate.id === view.line.id)
      return { line, face: view.face, marks: this.#estimateMarks(line) }
    })
    this.board = views.slice(0, BOARD_LIMIT)
    this.#renderBoard()
    if (!views.length) return this.#announce(t("nothing_to_export"))

    this.#announce(views.length > BOARD_LIMIT ? t("board_filled_partial", { limit: BOARD_LIMIT, total: views.length }) : t("board_filled", { count: views.length }))
  }

  removeView({ params: { index } }) {
    this.board.splice(index, 1)
    this.#renderBoard()
  }

  async download() {
    const blob = await this.#sheet()
    if (!blob) return

    const url = URL.createObjectURL(blob)
    const link = Object.assign(document.createElement("a"), { href: url, download: this.#filename() })
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
    this.#announce(t("downloaded"))
  }

  async attach() {
    if (!this.attachUrlValue) return

    this.attachTarget.disabled = true
    try {
      const blob = await this.#sheet()
      if (blob) this.#announce(await this.#attachSheet(blob))
    } catch {
      this.#announce(t("attach_failed"))
    } finally {
      this.attachTarget.disabled = false
    }
  }

  // Direct upload of the PNG, then the attachment to the draft; answers the message to show
  async #attachSheet(blob) {
    this.#announce(t("attaching"))
    const signedId = await new Promise((resolve, reject) => {
      new DirectUpload(new File([blob], this.#filename(), { type: "image/png" }), this.directUploadUrlValue)
        .create((error, uploaded) => (error ? reject(error) : resolve(uploaded.signed_id)))
    })
    const response = await fetch(this.attachUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({ signed_id: signedId })
    })
    const body = await response.json().catch(() => ({}))
    return response.ok ? body.message : body.error || t("attach_failed")
  }

  // ---- State ----

  // One entry per line of the estimate, with the markings of all its faces
  #groupLines(views) {
    const lines = new Map()
    views.forEach((view) => {
      const line = lines.get(view.line.id) || { ...view.line, color: view.color, kind: view.kind, template: view.template, markings: [] }
      view.markings.forEach((marking) => line.markings.push({ ...marking, face: view.face }))
      lines.set(view.line.id, line)
    })
    return [...lines.values()]
  }

  // The same logo attached to several markings is one file of the page, known by its name
  #estimateFiles() {
    const files = new Map()
    this.fileKeys = new Map()
    this.lines.flatMap((line) => line.markings).flatMap((marking) => marking.files).forEach((file) => {
      const key = `estimate-${file.filename}`
      this.fileKeys.set(file.id, key)
      if (!files.has(key)) files.set(key, { key, filename: file.filename, raw: file.path, drawable: file.drawable, light: file.light, session: false })
    })
    return files
  }

  #estimateMarks(line) {
    return line.markings.map((marking) => ({
      key: `marking-${marking.id}`,
      face: marking.face,
      zone: marking.zone,
      placed: marking.placed && Boolean(marking.box),
      added: false,
      format: marking.format,
      sizeCm: marking.size_cm,
      sizeSource: marking.size_source,
      box: marking.box,
      technique: marking.technique_label,
      text: marking.artwork_text,
      fileKey: this.fileKeys.get(marking.file_id) || null
    }))
  }

  // Each line keeps its session placements until « Rétablir »: coming back to it shows them again
  #selectLine(id) {
    if (this.line) this.sessions.set(this.line.id, { marks: this.marks, changed: this.changed, face: this.face })
    this.line = this.lines.find((line) => line.id === id)
    const session = this.sessions.get(id)
    if (session) return this.#showLine(session)

    this.#resetMarks()
    this.#showLine({ marks: this.marks, changed: false, face: this.marks.find((mark) => mark.placed)?.face || "front" })
  }

  #showLine({ marks, changed, face }) {
    Object.assign(this, { marks, changed, face })
    this.#renderLines()
    this.#renderFiles()
    this.#renderMarksAndStage()
    this.#renderBoard()
  }

  #resetMarks() {
    this.marks = this.#estimateMarks(this.line)
    this.changed = false
  }

  // A zone of the face already carries a placement other than the given one
  #occupied(face, code, except = null) {
    return this.marks.some((mark) => mark !== except && mark.face === face && mark.zone.code === code)
  }

  #mark(key) {
    return this.marks.find((mark) => mark.key === key)
  }

  #template(line = this.line) {
    return line.template ? this.templates[line.kind] : null
  }

  #zoneFormat(zone, format) {
    return { zone: { code: zone.code, name: zone.name }, format: format.code, sizeCm: format.size_cm, sizeSource: "format", box: format.box }
  }

  // A placement added for the session takes the file already shown on the line, else a file of the estimate
  #defaultFileKey() {
    const shown = this.marks.find((mark) => mark.fileKey)?.fileKey
    if (shown) return shown

    const drawable = [...this.files.values()].filter((file) => file.drawable)
    return (drawable.find((file) => file.light === Boolean(this.line.color.dark)) || drawable[0])?.key || null
  }

  #currentView() {
    return { line: this.line, face: this.face, marks: this.marks.map((mark) => ({ ...mark })) }
  }

  // ---- Files ----

  async #prepareFiles(files) {
    await Promise.all(files.filter((file) => file.drawable).map((file) => this.#prepare(file)))
    this.#renderFiles()
    this.#renderMarksAndStage()
    this.#renderBoard()
  }

  // Downsized, white background removed when asked, cropped to its visible part: what the views draw
  async #prepare(file) {
    try {
      const image = await loadImage(file.raw)
      const scale = Math.min(1, FILE_MAX_PX / Math.max(image.naturalWidth || FILE_MAX_PX, image.naturalHeight || FILE_MAX_PX))
      const width = Math.max(1, Math.round((image.naturalWidth || FILE_MAX_PX) * scale))
      const height = Math.max(1, Math.round((image.naturalHeight || FILE_MAX_PX) * scale))
      const canvas = Object.assign(document.createElement("canvas"), { width, height })
      const context = canvas.getContext("2d", { willReadFrequently: true })
      context.drawImage(image, 0, 0, width, height)
      if (this.removeBackgroundTarget.checked) this.#removeWhite(context, width, height)
      Object.assign(file, this.#crop(canvas, context), { failed: false })
    } catch {
      file.failed = true
    }
  }

  // A uniform light background, read in the four corners, turns transparent, with a soft edge
  #removeWhite(context, width, height) {
    const image = context.getImageData(0, 0, width, height)
    const data = image.data
    const corners = [0, width - 1, (height - 1) * width, height * width - 1].map((pixel) => data.slice(pixel * 4, pixel * 4 + 4))
    const [reference] = corners
    const distance = (offset) => Math.abs(data[offset] - reference[0]) + Math.abs(data[offset + 1] - reference[1]) + Math.abs(data[offset + 2] - reference[2])
    const uniform = corners.every((pixel) => pixel[3] > 200 && Math.abs(pixel[0] - reference[0]) + Math.abs(pixel[1] - reference[1]) + Math.abs(pixel[2] - reference[2]) < 30)
    if (!uniform || (reference[0] + reference[1] + reference[2]) / 3 <= 200) return

    for (let offset = 0; offset < data.length; offset += 4) {
      const gap = distance(offset)
      if (gap < 40) data[offset + 3] = 0
      else if (gap < 90) data[offset + 3] = Math.min(data[offset + 3], Math.round(((gap - 40) / 50) * 255))
    }
    context.putImageData(image, 0, 0)
  }

  #crop(canvas, context) {
    const { width, height } = canvas
    const data = context.getImageData(0, 0, width, height).data
    let [left, top, right, bottom] = [width, height, 0, 0]
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] <= 16) continue
        left = Math.min(left, x)
        right = Math.max(right, x)
        top = Math.min(top, y)
        bottom = Math.max(bottom, y)
      }
    }
    if (right <= left || bottom <= top) [left, top, right, bottom] = [0, 0, width - 1, height - 1]

    const cropped = Object.assign(document.createElement("canvas"), { width: right - left + 1, height: bottom - top + 1 })
    cropped.getContext("2d").drawImage(canvas, left, top, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height)
    return { src: cropped.toDataURL("image/png") }
  }

  // ---- Drawing ----

  // The face's silhouette filled with the colourway's hex, the placed markings in its markings group. Its ids are
  // suffixed: the same template is drawn on the stage, on the sheet and in the exported image
  #viewSvg(view) {
    const suffix = `v${++this.renderCount}`
    const markup = this.#template(view.line).faces[view.face].svg
      .replace(/(id="|url\(#)(mockup-[\w-]+)/g, (_, prefix, id) => `${prefix}${id}-${suffix}`)
    const svg = new DOMParser().parseFromString(markup, "image/svg+xml").documentElement
    if (view.line.color.hex) svg.setAttribute("color", view.line.color.hex)
    svg.setAttribute("role", "img")
    svg.setAttribute("aria-label", this.#viewLabel(view))
    svg.setAttribute("class", "size-full")
    const group = svg.querySelector("[data-mockup-markings]")
    view.marks.filter((mark) => mark.placed && mark.face === view.face).forEach((mark) => group.appendChild(this.#markingNode(mark, view.line)))
    return new XMLSerializer().serializeToString(svg)
  }

  #markingNode(mark, line) {
    const { x, y, width, height } = mark.box
    const file = mark.fileKey && this.files.get(mark.fileKey)
    if (file?.src) {
      return this.#svgNode("image", { href: file.src, x, y, width, height, preserveAspectRatio: "xMidYMin meet" })
    }

    // No drawable file: the frame of the marking, with its text when it is a text marking
    const ink = line.color.dark ? "#FFFFFF" : INK
    const frame = this.#svgNode("g", {})
    frame.appendChild(this.#svgNode("rect", { x, y, width, height, rx: 4, fill: "none", stroke: ink, "stroke-opacity": 0.55, "stroke-width": 1.5, "stroke-dasharray": "4 3" }))
    if (width > 34) {
      const label = this.#svgNode("text", { x: x + width / 2, y: y + height / 2 + 3, "text-anchor": "middle", "font-family": "Arial, sans-serif", "font-size": width > 60 ? 9 : 7, "font-weight": 700, fill: ink, "fill-opacity": 0.7 })
      label.textContent = (mark.text || t("logo_to_come")).slice(0, 40)
      frame.appendChild(label)
    }
    return frame
  }

  #svgNode(name, attributes) {
    const node = document.createElementNS(SVG_NS, name)
    Object.entries(attributes).forEach(([attribute, value]) => node.setAttribute(attribute, value))
    return node
  }

  #viewLabel(view) {
    return [this.#lineName(view.line), view.line.color.name, t(`faces.${view.face}`)].filter(Boolean).join(" · ")
  }

  // The article as the estimate names it (its description), its reference and colourway otherwise
  #lineName(line) {
    return line.description || line.label
  }

  // Colourway, face and placements; a placement named after its face (« Dos ») is not repeated
  #viewCaption(view) {
    const face = t(`faces.${view.face}`)
    const marks = this.#marksSummary(view)
    return [view.line.color.name, marks.startsWith(face) ? marks : `${face} · ${marks}`].filter(Boolean).join(" · ")
  }

  #marksSummary(view) {
    const marks = view.marks.filter((mark) => mark.placed && mark.face === view.face)
    if (!marks.length) return t("no_marking")

    return marks.map((mark) => [mark.zone.name, mark.format].filter(Boolean).join(" ")).join(", ")
  }

  // ---- Panels ----

  #renderLines() {
    this.linesTarget.innerHTML = this.lines.map((line) => {
      const on = line.id === this.line.id
      const swatch = line.color.hex ? `background:${escapeHtml(line.color.hex)}` : ""
      return `<button type="button" role="radio" aria-checked="${on}" data-action="erp--mockup#pickLine" data-erp--mockup-id-param="${line.id}"
        class="flex w-full cursor-pointer items-center gap-3 rounded-erp-field border px-3 py-2.5 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erp-black ${on ? "border-erp-ink bg-erp-surface-soft" : "border-erp-line hover:border-erp-line-strong"}">
        <span class="size-6 shrink-0 rounded-full border border-erp-line-strong bg-erp-field" style="${swatch}" aria-hidden="true"></span>
        <span class="flex min-w-0 flex-col">
          <b class="truncate text-sm">${escapeHtml(this.#lineName(line))}</b>
          <small class="text-[13px] text-erp-muted">${escapeHtml([line.color.name, t("pieces", { count: line.quantity })].filter(Boolean).join(" · "))}</small>
        </span>
      </button>`
    }).join("")
  }

  #renderFiles() {
    const files = [...this.files.values()]
    if (!files.length) {
      this.filesTarget.innerHTML = `<li class="rounded-erp-field border-2 border-dashed border-erp-line-dashed p-4 text-center text-[13px] text-erp-muted">${escapeHtml(t("files_empty"))}</li>`
      return
    }

    this.filesTarget.innerHTML = files.map((file) => {
      const notes = [file.session ? t("file_session") : t("file_estimate"), file.light && t("file_light"), !file.drawable && t("file_not_drawable"), file.failed && t("file_unreadable")]
      return `<li class="flex min-w-0 items-center gap-3 rounded-erp-field border border-erp-line px-3 py-2">
        <span class="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-erp-line bg-erp-bg">${file.src ? `<img src="${escapeHtml(file.src)}" alt="" class="max-h-full max-w-full">` : '<i class="lni lni-file-multiple text-lg text-erp-muted" aria-hidden="true"></i>'}</span>
        <span class="flex min-w-0 flex-col"><b class="truncate text-sm" title="${escapeHtml(file.filename)}">${escapeHtml(file.filename)}</b>
          <small class="text-[13px] text-erp-muted">${escapeHtml(notes.filter(Boolean).join(" · "))}</small></span>
      </li>`
    }).join("")
  }

  #renderMarksAndStage() {
    this.#renderFaces()
    this.#renderMarks()
    this.#renderStage()
  }

  #renderFaces() {
    const template = this.#template()
    this.facesTarget.hidden = !template
    if (!template) return

    this.facesTarget.innerHTML = FACES.map((face) => {
      const on = face === this.face
      const count = this.marks.filter((mark) => mark.placed && mark.face === face).length
      return `<button type="button" role="radio" aria-checked="${on}" data-action="erp--mockup#pickFace" data-erp--mockup-face-param="${face}"
        class="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-3.5 text-[13px] font-bold focus-visible:outline-2 focus-visible:outline-erp-black ${on ? "bg-erp-black text-white" : "text-erp-ink"}">
        ${escapeHtml(t(`faces.${face}`))}${count ? `<span class="text-xs opacity-70">${count}</span>` : ""}</button>`
    }).join("")
  }

  #renderMarks() {
    this.restoreTarget.hidden = !this.changed
    this.addMarkTarget.disabled = !this.#template()
    this.marksTarget.innerHTML = this.marks.length
      ? this.marks.map((mark) => this.#markItem(mark)).join("")
      : `<li class="rounded-erp-field border-2 border-dashed border-erp-line-dashed p-4 text-center text-[13px] text-erp-muted">${escapeHtml(t("no_placement"))}</li>`
  }

  #markItem(mark) {
    const zoneLabel = [mark.zone.name, mark.face && t(`faces.${mark.face}`)].filter(Boolean).join(" · ")
    const body = mark.added ? this.#addedMarkFields(mark) : `<p class="m-0 text-[13px] text-erp-ink-2">${escapeHtml(this.#markDetail(mark))}</p>`
    return `<li class="flex flex-col gap-2 rounded-erp-field border p-3 ${mark.placed && mark.face === this.face ? "border-erp-ink" : "border-erp-line"}">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0"><b class="text-sm">${escapeHtml(zoneLabel)}</b>
          <small class="block text-[13px] text-erp-muted">${escapeHtml(mark.added ? t("added_placement") : mark.technique)}</small></div>
        <button type="button" class="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-erp-muted hover:bg-erp-surface-soft hover:text-erp-danger focus-visible:outline-2 focus-visible:outline-erp-black"
          aria-label="${escapeHtml(t("remove_placement", { zone: mark.zone.name }))}" data-action="erp--mockup#removeMark" ${markParams(mark)}><i class="lni lni-trash-3 text-base leading-none" aria-hidden="true"></i></button>
      </div>
      ${body}
      ${mark.placed ? this.#fileSelect(mark) : ""}
    </li>`
  }

  // A placement added for the session chooses its zone, on either face, and its format
  #addedMarkFields(mark) {
    const faces = this.#template().faces
    const zones = FACES.map((face) => `<optgroup label="${escapeHtml(t(`faces.${face}`))}">${faces[face].zones.map((zone) =>
      option(`${face}|${zone.code}`, `${t(`faces.${face}`)} · ${zone.name}`, face === mark.face && zone.code === mark.zone.code,
        this.#occupied(face, zone.code, mark))).join("")}</optgroup>`).join("")
    const formats = faces[mark.face].zones.find((zone) => zone.code === mark.zone.code).formats
      .map((format) => option(format.code, format.code, format.code === mark.format)).join("")
    return `<div class="grid grid-cols-[minmax(0,1fr)_88px] gap-2">
      <select class="${SELECT}" aria-label="${escapeHtml(t("zone"))}" data-action="change->erp--mockup#changeZone" ${markParams(mark)}>${zones}</select>
      <select class="${SELECT}" aria-label="${escapeHtml(t("format"))}" data-action="change->erp--mockup#changeFormat" ${markParams(mark)}>${formats}</select></div>`
  }

  #fileSelect(mark) {
    const files = [...this.files.values()].filter((file) => file.drawable && !file.failed)
    const options = [option("", t("no_file"), false), ...files.map((file) => option(file.key, file.filename, file.key === mark.fileKey))].join("")
    return `<select class="${SELECT}" aria-label="${escapeHtml(t("file"))}" data-action="change->erp--mockup#changeFile" ${markParams(mark)}>${options}</select>`
  }

  #markDetail(mark) {
    if (!mark.placed) return this.line.template ? t("unplaced") : t("unplaced_no_template")

    const size = { width: cm(mark.sizeCm.width), height: cm(mark.sizeCm.height) }
    return [mark.format, mark.sizeSource === "zone" ? t("size_zone", size) : t("size", size)].filter(Boolean).join(" · ")
  }

  #renderStage() {
    const view = this.#currentView()
    this.stageTitleTarget.textContent = this.#lineName(this.line)
    this.addToBoardTarget.disabled = !this.line.template || this.board.length >= BOARD_LIMIT
    if (!this.line.template) {
      this.stageSubtitleTarget.textContent = ""
      this.stageTarget.innerHTML = `<p class="m-0 p-6 text-center text-sm font-semibold text-erp-muted">${escapeHtml(t("no_template"))}</p>`
      return
    }

    this.stageSubtitleTarget.textContent = this.#viewCaption(view)
    this.stageTarget.innerHTML = this.#viewSvg(view)
  }

  #renderBoard() {
    this.boardCountTarget.textContent = `(${this.board.length})`
    const tiles = this.board.map((view, index) => `<li class="relative flex min-w-0 flex-col gap-1 rounded-erp-field border border-erp-line bg-erp-bg p-2">
      <div class="aspect-[400/470]">${this.#viewSvg(view)}</div>
      <b class="truncate text-[13px]">${escapeHtml(this.#lineName(view.line))}</b>
      <small class="truncate text-xs text-erp-muted">${escapeHtml(this.#viewCaption(view))}</small>
      <button type="button" class="absolute top-2 right-2 inline-flex size-7 cursor-pointer items-center justify-center rounded-full border border-erp-line bg-erp-surface text-erp-ink hover:border-erp-ink focus-visible:outline-2 focus-visible:outline-erp-black"
        aria-label="${escapeHtml(t("remove_view"))}" data-action="erp--mockup#removeView" data-erp--mockup-index-param="${index}"><i class="lni lni-xmark text-sm leading-none" aria-hidden="true"></i></button>
    </li>`)
    if (this.board.length < BOARD_LIMIT) {
      tiles.push(`<li class="flex min-h-32 items-center justify-center rounded-erp-field border-2 border-dashed border-erp-line-dashed p-4 text-center text-[13px] text-erp-muted">${escapeHtml(this.board.length ? t("board_slot") : t("board_slot_empty"))}</li>`)
    }
    this.boardTarget.innerHTML = tiles.join("")
    if (this.hasAddToBoardTarget) this.addToBoardTarget.disabled = !this.line.template || this.board.length >= BOARD_LIMIT
  }

  #announce(message) {
    this.statusTarget.textContent = message || ""
  }

  // ---- Export ----

  #filename() {
    const reference = String(this.estimate.reference || "devis").replace(/[^\w-]+/g, "-")
    return `mockup-${reference}.png`
  }

  // The sheet (the views of the board, the view shown when the board is empty) as a low definition PNG, with the
  // printed mention « Visuel indicatif, ne vaut pas BAT »
  async #sheet() {
    // A file still loading would be exported as an empty frame
    await this.preparing
    const views = this.board.length ? this.board : this.line.template ? [this.#currentView()] : []
    if (!views.length) {
      this.#announce(t("nothing_to_export"))
      return null
    }

    try {
      const image = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(this.#sheetSvg(views))}`)
      const scale = Math.min(1, SHEET_MAX_WIDTH / image.naturalWidth)
      const canvas = Object.assign(document.createElement("canvas"), { width: Math.round(image.naturalWidth * scale), height: Math.round(image.naturalHeight * scale) })
      canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height)
      return await new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("empty"))), "image/png"))
    } catch {
      this.#announce(t("export_failed"))
      return null
    }
  }

  #sheetSvg(views) {
    const date = new Date().toLocaleDateString("fr-FR")
    return sheetSvg({
      tiles: views.map((view) => ({
        svg: this.#viewSvg(view),
        title: this.#lineName(view.line),
        caption: this.#viewCaption(view)
      })),
      title: t("sheet_title", { customer: this.estimate.customer }),
      subtitle: [this.estimate.title, t("sheet_reference", { reference: this.estimate.reference }), date].filter(Boolean).join(" · "),
      brand: "SOFIDIAL",
      mention: t("mention"),
      detail: t("mention_detail"),
      watermark: t("watermark")
    })
  }
}
